import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Send,
  CheckCircle,
  MousePointer,
  Target,
  Mail,
  MessageSquare,
  Smartphone,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import AdminHeader from '../../components/AdminHeader';
import { apiClient } from '../../App';

const PERIODS = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
];

const EVENT_LABELS = {
  confirmation_reminder_due: '12h Reminder',
  final_reminder_due: '2h Final',
  waitlist_promoted: 'Waitlist Promoted',
  game_canceled: 'Game Canceled',
};

const EVENT_CONVERSION_LABELS = {
  confirmation_reminder_due: 'Confirmed',
  final_reminder_due: 'Confirmed',
  waitlist_promoted: 'Joined',
  game_canceled: 'Re-engaged',
};

const CHANNEL_ICONS = {
  in_app: MessageSquare,
  email: Mail,
  sms: Smartphone,
};

const CHANNEL_COLORS = {
  in_app: 'bg-violet-50 text-violet-700 border-violet-200',
  email: 'bg-blue-50 text-blue-700 border-blue-200',
  sms: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function pct(value) {
  return value > 0 ? `${(value * 100).toFixed(1)}%` : '—';
}

export default function NotificationPerformance() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/admin/notifications/performance?period=${period}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load performance data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasData = data && data.total_sent > 0;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notification-performance-page">
      <AdminHeader title="Performance" onRefresh={fetchData} refreshing={loading} />
      
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Period Selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-gray-500" />
            <h2 className="text-base font-semibold text-gray-800">Notification Effectiveness</h2>
          </div>
          <div className="flex gap-1 bg-white rounded-lg border p-0.5" data-testid="period-selector">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                  ${period === p.value
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" data-testid="performance-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-cards">
              <SummaryCard
                icon={Send}
                label="Sent"
                value={data.total_sent}
                testId="total-sent"
              />
              <SummaryCard
                icon={Target}
                label="Converted"
                value={data.total_converted}
                testId="total-converted"
              />
              <SummaryCard
                icon={TrendingUp}
                label="Conversion Rate"
                value={pct(data.overall_conversion_rate)}
                highlight={data.overall_conversion_rate > 0.5}
                testId="overall-conversion-rate"
              />
              <SummaryCard
                icon={CheckCircle}
                label="Events Tracked"
                value={data.by_event?.length || 0}
                testId="events-tracked"
              />
            </div>

            {/* Event Breakdown Table */}
            <div className="bg-white rounded-xl border overflow-hidden" data-testid="event-table">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">By Event Type</h3>
              </div>
              
              {/* Desktop Table */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="px-4 py-2.5 font-medium">Event</th>
                      <th className="px-4 py-2.5 font-medium text-right">Sent</th>
                      <th className="px-4 py-2.5 font-medium text-right">Delivered</th>
                      <th className="px-4 py-2.5 font-medium text-right">Clicked</th>
                      <th className="px-4 py-2.5 font-medium text-right">Converted</th>
                      <th className="px-4 py-2.5 font-medium text-right">Click %</th>
                      <th className="px-4 py-2.5 font-medium text-right">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_event?.map(evt => (
                      <tr key={evt.event_type} className="border-b last:border-0 hover:bg-gray-50" data-testid={`event-row-${evt.event_type}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{EVENT_LABELS[evt.event_type] || evt.event_type}</div>
                          <div className="text-xs text-gray-400">{EVENT_CONVERSION_LABELS[evt.event_type] || 'Action'}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{evt.sent}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{evt.delivered}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{evt.clicked}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{evt.converted}</td>
                        <td className="px-4 py-3 text-right">
                          <RateBadge value={evt.click_rate} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RateBadge value={evt.conversion_rate} highlight />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden divide-y">
                {data.by_event?.map(evt => (
                  <div key={evt.event_type} className="px-4 py-3 space-y-2" data-testid={`event-card-${evt.event_type}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 text-sm">{EVENT_LABELS[evt.event_type] || evt.event_type}</div>
                        <div className="text-xs text-gray-400">{EVENT_CONVERSION_LABELS[evt.event_type]}</div>
                      </div>
                      <RateBadge value={evt.conversion_rate} highlight />
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <MiniStat label="Sent" value={evt.sent} />
                      <MiniStat label="Delivered" value={evt.delivered} />
                      <MiniStat label="Clicked" value={evt.clicked} />
                      <MiniStat label="Converted" value={evt.converted} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Channel Breakdown */}
            {data.by_channel?.length > 0 && (
              <div className="bg-white rounded-xl border overflow-hidden" data-testid="channel-table">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">By Channel</h3>
                </div>
                <div className="divide-y">
                  {data.by_channel.map(ch => {
                    const Icon = CHANNEL_ICONS[ch.channel] || MessageSquare;
                    const colorClass = CHANNEL_COLORS[ch.channel] || 'bg-gray-50 text-gray-700 border-gray-200';
                    const convRate = ch.sent > 0 ? ch.converted / ch.sent : 0;
                    return (
                      <div key={ch.channel} className="px-4 py-3 flex items-center justify-between" data-testid={`channel-row-${ch.channel}`}>
                        <div className="flex items-center gap-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
                            <Icon className="w-3.5 h-3.5" />
                            {ch.channel.replace('_', '-').toUpperCase()}
                          </span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-gray-500">{ch.sent} sent</span>
                          <span className="text-gray-500">{ch.delivered} delivered</span>
                          <RateBadge value={convRate} highlight />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!hasData && (
              <div className="bg-white rounded-xl border p-8 text-center" data-testid="empty-state">
                <TrendingUp className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-gray-700 mb-1">No performance data yet</h3>
                <p className="text-xs text-gray-400 max-w-md mx-auto">
                  Performance tracking starts when notifications are sent through the dispatcher. 
                  Data will appear here for confirmation reminders, final reminders, waitlist promotions, and cancellation re-engagement.
                </p>
              </div>
            )}

            {/* How it works */}
            <div className="bg-white rounded-xl border p-4" data-testid="how-it-works">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Conversion Definitions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-500">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 flex-shrink-0">12h Reminder</span>
                  <span>User confirms attendance</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 flex-shrink-0">2h Final</span>
                  <span>User confirms attendance</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 flex-shrink-0">Waitlist</span>
                  <span>User joins / accepts the promoted spot</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600 flex-shrink-0">Canceled</span>
                  <span>User joins another game within 24h</span>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, highlight, testId }) {
  return (
    <div className="bg-white rounded-xl border p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${highlight ? 'text-green-600' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

function RateBadge({ value, highlight }) {
  if (!value || value === 0) return <span className="text-xs text-gray-300 font-mono">—</span>;
  const pctStr = `${(value * 100).toFixed(1)}%`;
  const color = highlight
    ? value >= 0.5 ? 'bg-green-50 text-green-700' : value >= 0.2 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'
    : 'bg-gray-50 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${color}`}>
      {pctStr}
    </span>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-400">{label}</div>
      <div className="font-mono text-sm font-medium text-gray-700">{value}</div>
    </div>
  );
}
